/* SUMMARY
  * Imports
  * CustomerNoteCallout: inline, always-visible echo of a customer's role note
*/

/* Imports */
import { Typography } from "@material-tailwind/react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import { t } from "i18next";
/***/

/**
 * isBlankNote: a note is "absent" when it is undefined, empty, or only whitespace.
 *
 * Exported because CustomerNoteDialog and every caller that decides whether to *open* the dialog
 * needs the same answer. Three call sites each re-implementing `note && note.trim()` is how one of
 * them ends up popping an empty modal for a customer whose note is a stray newline.
 */
export function isBlankNote(note?: string | null): boolean {
  return !note || note.trim().length === 0;
}

/**
 * CustomerNoteCallout: the quiet, persistent half of the note treatment.
 *
 * The pop-up fires once on entry; this stays behind so the note can be re-read without reloading.
 * It is deliberately a sibling of CustomerInfoCard rather than a part of it -- that card is shared
 * with the quotation and prospect forms, which are out of scope for this feature.
 *
 * Renders nothing at all when there is no note: no border, no icon, no empty gap.
 *
 * @param note - the note text
 * @param customerName - shown as context when the callout sits away from the customer card
 * @param className - additional classnames
 */
export default function CustomerNoteCallout({
  note,
  customerName,
  className = ""
}: {
  note?: string | null,
  customerName?: string,
  className?: string
}) {
  if (isBlankNote(note)) return null;

  return (
    <div className={`m-3 flex gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 ${className}`}
         data-testid="customer-note-callout">
      <ExclamationTriangleIcon className="h-[20px] w-[20px] shrink-0 text-amber-600" />

      <div className="flex flex-col gap-1">
        <Typography variant="small" className="font-semibold text-amber-900">
          {customerName
            ? t("client:noteAlert:calloutTitleFor", { name: customerName })
            : t("client:noteAlert:calloutTitle")}
        </Typography>

        {/* whitespace-pre-line: planners write these as checklists, one instruction per line. */}
        <Typography className="whitespace-pre-line break-words text-amber-900">
          {note}
        </Typography>
      </div>
    </div>
  );
}
/***/
